import { useState, useEffect } from 'react';
import debounce from 'lodash.debounce';

type ScrollCallback = (percent: number) => void;

const isScrollable = (element: HTMLElement) => {
  return element.scrollHeight > element.clientHeight;
};

const scrollHandler = (onScroll: ScrollCallback) => (event: Event) => {
  if (!(event.target instanceof HTMLElement)) return;
  const containerHeight = event.target.clientHeight;
  const contentHeight = event.target.scrollHeight;
  const contentScrollY = event.target.scrollTop;
  const percentage = (contentScrollY / (contentHeight - containerHeight)) * 100;
  onScroll(Math.round(percentage));
};

const useScrollObserver = (onScroll: ScrollCallback) => {
  const [element, setElement] = useState<HTMLElement | null | undefined>(null);

  useEffect(() => {
    if (!element) return;
    if (!isScrollable(element)) return;

    const debouncedHandler = debounce(scrollHandler(onScroll), 50, {
      leading: true,
      trailing: true,
    });

    element.addEventListener('scroll', debouncedHandler);
    element.dispatchEvent(new Event('scroll'));

    return () => element.removeEventListener('scroll', debouncedHandler);
  }, [element, onScroll]);

  return { setElement };
};

export default useScrollObserver;
